// src/lib/fixedServiceWorkerUtils.js
// Fixed Service Worker management without infinite loops

const SW_JUST_UPDATED_KEY = 'sw-just-updated'
const UPDATE_COOLDOWN_MS = 30000 // 30 seconds cooldown after update

class FixedServiceWorkerManager {
  constructor() {
    this.registration = null
    this.isSupported = 'serviceWorker' in navigator
    this.updateAvailable = false
    this.isChecking = false
    this.isRegistering = false
    this.updateCheckInterval = null
    this.justUpdated = this.checkJustUpdated()
  }

  // Check if we just updated (to prevent update loop)
  checkJustUpdated() {
    try {
      const timestamp = localStorage.getItem(SW_JUST_UPDATED_KEY)
      if (timestamp) {
        const elapsed = Date.now() - parseInt(timestamp, 10)
        if (elapsed < UPDATE_COOLDOWN_MS) {
          console.log(`[SW] Update cooldown active (${Math.round((UPDATE_COOLDOWN_MS - elapsed) / 1000)}s remaining)`)
          return true
        }
        // Cooldown expired, clear flag
        localStorage.removeItem(SW_JUST_UPDATED_KEY)
      }
    } catch (e) {
      // localStorage not available
    }
    return false
  }

  // Mark that we just updated
  markJustUpdated() {
    try {
      localStorage.setItem(SW_JUST_UPDATED_KEY, Date.now().toString())
      this.justUpdated = true
    } catch (e) {
      // localStorage not available
    }
  }

  // Clear the just updated flag
  clearJustUpdated() {
    try {
      localStorage.removeItem(SW_JUST_UPDATED_KEY)
      this.justUpdated = false
    } catch (e) {
      // localStorage not available
    }
  }

  // Register the fixed service worker
  async register() {
    if (!this.isSupported) {
      console.warn('[SW] Service workers not supported')
      return false
    }

    // Prevent multiple registrations
    if (this.registration) {
      console.log('[SW] Service worker already registered')
      return true
    }

    // Check if already registering
    if (this.isRegistering) {
      console.log('[SW] Service worker registration already in progress')
      return false
    }

    this.isRegistering = true

    try {
      // Check if there's already an active service worker
      const existingRegistrations = await navigator.serviceWorker.getRegistrations()
      const existingSW = existingRegistrations.find(reg => reg.scope === window.location.origin + '/')
      
      if (existingSW && existingSW.active) {
        console.log('[SW] Using existing service worker')
        this.registration = existingSW
        this.setupEventListeners()
        return true
      }

      // Clean up any other service workers
      if (existingRegistrations.length > 0) {
        console.log('[SW] Cleaning up existing service workers')
        await Promise.all(existingRegistrations.map(reg => reg.unregister()))
        
        // Clear old caches
        const cacheNames = await caches.keys()
        await Promise.all(cacheNames.map(name => caches.delete(name)))
        
        // Wait a bit to ensure cleanup is complete
        await new Promise(resolve => setTimeout(resolve, 100))
      }

      // Register the fixed service worker with cache-busting to force update checks
      // updateViaCache: 'none' forces browser to always check for updates
      this.registration = await navigator.serviceWorker.register('/sw-fixed.js', {
        scope: '/',
        updateViaCache: 'none' // Force browser to always check for updates
      })

      console.log('[SW] Fixed service worker registered successfully')

      // Wait for the service worker to be ready
      await this.waitForServiceWorkerReady()

      // Set up event listeners
      this.setupEventListeners()

      return true
    } catch (error) {
      console.error('[SW] Fixed service worker registration failed:', error)
      return false
    } finally {
      this.isRegistering = false
    }
  }

  // Wait for service worker to be ready
  async waitForServiceWorkerReady() {
    return new Promise((resolve) => {
      if (this.registration.installing) {
        const installingWorker = this.registration.installing
        installingWorker.addEventListener('statechange', () => {
          if (installingWorker && installingWorker.state === 'installed') {
            resolve()
          }
        })
      } else if (this.registration.waiting) {
        const waitingWorker = this.registration.waiting
        waitingWorker.addEventListener('statechange', () => {
          if (waitingWorker && waitingWorker.state === 'activated') {
            resolve()
          }
        })
      } else if (this.registration.active) {
        resolve()
      } else {
        // Fallback timeout
        setTimeout(resolve, 1000)
      }
    })
  }

  // Set up event listeners
  setupEventListeners() {
    if (!this.registration) return

    // Listen for updates
    this.registration.addEventListener('updatefound', () => {
      // Skip if we just updated (prevent loop)
      if (this.justUpdated) {
        console.log('[SW] Update found but in cooldown period, ignoring')
        return
      }

      console.log('[SW] Update found - new service worker installing')
      const newWorker = this.registration.installing
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker && newWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // Skip if we just updated (prevent loop)
              if (this.justUpdated) {
                console.log('[SW] New SW installed but in cooldown period, ignoring')
                return
              }
              
              // There's a new service worker waiting
              console.log('[SW] New service worker installed and waiting')
              this.updateAvailable = true
              // Dispatch custom event for React components
              window.dispatchEvent(new CustomEvent('sw-update-available'))
            } else {
              // First time installation
              console.log('[SW] Service worker installed for the first time')
            }
          } else if (newWorker && newWorker.state === 'activated') {
            console.log('[SW] New service worker activated')
          }
        })
      }
    })

    // Periodically check for updates (every 5 minutes instead of 1 minute to reduce noise)
    this.updateCheckInterval = setInterval(() => {
      // Skip periodic checks during cooldown
      if (this.justUpdated) {
        this.justUpdated = this.checkJustUpdated() // Re-check if cooldown expired
        return
      }
      
      if (this.registration) {
        this.registration.update().catch(err => {
          console.warn('[SW] Periodic update check failed:', err)
        })
      }
    }, 300000) // Check every 5 minutes

    // Listen for controller changes - only reload if there's actually an update AND not in cooldown
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (this.updateAvailable && !this.justUpdated) {
        console.log('[SW] Controller changed, reloading for update')
        window.location.reload()
      }
    })

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (!event.data || !event.data.type) return

      switch (event.data.type) {
        case 'UPDATE_AVAILABLE':
          // Skip if we just updated (prevent loop)
          if (this.justUpdated) {
            console.log('[SW] Update message received but in cooldown period, ignoring')
            return
          }

          console.log('[SW] Update available message received:', event.data.version)
          this.updateAvailable = true
          // Dispatch custom event for React components
          window.dispatchEvent(new CustomEvent('sw-update-available', {
            detail: { version: event.data.version }
          }))
          break

        case 'STALE_DEPLOYMENT_DETECTED':
          // A chunk 404'd - deployment changed while user had app open
          console.warn('[SW] Stale deployment detected:', event.data.failedUrl)
          console.log('[SW] Message:', event.data.message)

          // Dispatch event for React components to show a reload prompt
          window.dispatchEvent(new CustomEvent('sw-stale-deployment', {
            detail: {
              failedUrl: event.data.failedUrl,
              message: event.data.message
            }
          }))

          // Also mark update as available since deployment changed
          this.updateAvailable = true
          window.dispatchEvent(new CustomEvent('sw-update-available', {
            detail: { reason: 'stale-deployment' }
          }))
          break

        default:
          // Ignore other message types
          break
      }
    })
  }

  // Check for updates (called automatically by service worker)
  async checkForUpdates() {
    // Skip if in cooldown period
    if (this.justUpdated) {
      this.justUpdated = this.checkJustUpdated() // Re-check
      if (this.justUpdated) {
        console.log('[SW] Update check skipped - in cooldown period')
        return false
      }
    }

    if (this.isChecking || !this.registration) {
      return false
    }

    this.isChecking = true

    try {
      // Force service worker registration update check
      // This bypasses the browser's cache and checks the server for a new service worker file
      await this.registration.update()

      // Also check for app version updates via service worker message
      if (this.registration.active) {
        const result = await this.sendMessageToSW({
          type: 'CHECK_FOR_UPDATES'
        })

        return result && result.updated
      }

      return false
    } catch (error) {
      console.warn('[SW] Update check failed:', error)
      return false
    } finally {
      this.isChecking = false
    }
  }

  // Send message to service worker
  async sendMessageToSW(message) {
    return new Promise((resolve) => {
      if (!this.registration || !this.registration.active) {
        resolve(null)
        return
      }

      const messageChannel = new MessageChannel()
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data)
      }

      this.registration.active.postMessage(message, [messageChannel.port2])
    })
  }

  // Skip waiting and activate new service worker
  async skipWaiting() {
    if (this.registration && this.registration.waiting) {
      // Mark that we're updating to prevent loop
      this.markJustUpdated()
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    }
  }

  // Clear all caches
  async clearCaches() {
    if (this.registration && this.registration.active) {
      this.registration.active.postMessage({ type: 'CLEAR_CACHE' })
    }
  }

  // Get cache status
  async getCacheStatus() {
    return this.sendMessageToSW({ type: 'GET_CACHE_STATUS' })
  }

  // Check if service worker is controlling the page
  isControlling() {
    return navigator.serviceWorker.controller !== null
  }

  // Get service worker state
  getState() {
    return {
      isSupported: this.isSupported,
      isRegistered: !!this.registration,
      isControlling: this.isControlling(),
      updateAvailable: this.updateAvailable,
      isChecking: this.isChecking,
      inCooldown: this.justUpdated
    }
  }

  // Cleanup intervals
  cleanup() {
    if (this.updateCheckInterval) {
      clearInterval(this.updateCheckInterval)
      this.updateCheckInterval = null
    }
  }
}

// Create singleton instance
export const fixedServiceWorkerManager = new FixedServiceWorkerManager()

// NOTE: Auto-registration removed to prevent double registration
// Registration is now handled exclusively in main.jsx

// Export utility functions
export const registerFixedServiceWorker = () => fixedServiceWorkerManager.register()
export const checkForUpdates = () => fixedServiceWorkerManager.checkForUpdates()
export const skipWaiting = () => fixedServiceWorkerManager.skipWaiting()
export const clearCaches = () => fixedServiceWorkerManager.clearCaches()
export const getCacheStatus = () => fixedServiceWorkerManager.getCacheStatus()
export const getSWState = () => fixedServiceWorkerManager.getState()
